import { takeLatest, put, call, all, fork } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { simpleRequestsLoader } from 'modules/app/utils';
import * as coreActions from 'modules/steps/actions/core';
import * as detailsActions from 'modules/steps/actions/details';
import * as types from 'modules/steps/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';

import stepsDashboardSaga from './dashboard';
import stepsDetailsSaga from './details';

const simpleRequests = {
  [types.PATCH_STEPS_MODE_REQUEST]: {
    callbacks: {
      success: (data, meta, { payload: { dealerId, mode } }) =>
        coreActions.patchStepsModeSuccess({ dealerId, mode, ...data }, meta),
    },
    notifications: {
      success: {
        id: 'page.steps.patchMode.success',
        defaultMessage: 'The step has been updated successfully.',
      },
      failure: {
        id: 'page.steps.patchMode.failure',
        defaultMessage: 'An error occurred while updating the step.',
      },
    },
  },
  [types.REORDER_STEPS_REQUEST]: {
    callbacks: {
      success: (steps, meta, { payload: { dealerId, mode } }) =>
        coreActions.reorderStepsSuccess({ dealerId, mode, steps }, meta),
    },
    notifications: {
      success: {
        id: 'page.steps.reorder.success',
        defaultMessage: 'The steps have been reordered successfully.',
      },
      failure: {
        id: 'page.steps.reorder.failure',
        defaultMessage: 'An error occurred while reordering the steps.',
      },
    },
  },
  [types.CREATE_STEP_REQUEST]: {
    callbacks: {
      success: (step, meta, { payload: { dealerId, mode } }) =>
        coreActions.createStepSuccess({ dealerId, mode, step }, meta),
    },
    notifications: {
      success: {
        id: 'page.steps.create.success',
        defaultMessage: 'The step has been created successfully.',
      },
      failure: {
        id: 'page.steps.create.failure',
        defaultMessage: 'An error occurred while creating the step.',
      },
    },
  },
  [types.UPDATE_STEP_REQUEST]: {
    callbacks: {
      success: (step, meta, { payload: { dealerId, mode } }) =>
        coreActions.updateStepSuccess({ dealerId, mode, step }, meta),
    },
    notifications: {
      success: {
        id: 'page.steps.update.success',
        defaultMessage: 'The step has been updated successfully.',
      },
      failure: {
        id: 'page.steps.update.failure',
        defaultMessage: 'An error occurred while updating the step.',
      },
    },
  },
  [types.DELETE_STEP_REQUEST]: {
    callbacks: {
      success: (steps, meta, { payload }) => coreActions.deleteStepSuccess({ ...payload.data, steps }, meta),
    },
    notifications: {
      success: {
        id: 'page.steps.delete.success',
        defaultMessage: 'The step has been deleted successfully.',
      },
      failure: {
        id: 'page.steps.delete.failure',
        defaultMessage: 'An error occurred while deleting the step.',
      },
    },
  },
};

function* patchInterventionStep({ payload, meta }) {
  const { request } = payload;
  const { origin } = meta;

  try {
    const response = yield call(restClient.request, request);
    yield put(detailsActions.patchInterventionStepSuccess(response.data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: `customSteps.update_${origin}.success`,
          defaultMessage: `The file ${origin} has been updated with success.`,
        },
      })
    );
  } catch (errorMessage) {
    yield put(detailsActions.patchInterventionStepFailure(errorMessage));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: `customSteps.update_${origin}.failure`,
          defaultMessage: `An error occurred while updating the file ${origin}.`,
        },
      })
    );
  }
}

/** @param {import('../types').SaveFollowUpConfigAction} action */
function* saveFollowUpConfig({ payload }) {
  const { request, dealerId } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(detailsActions.saveFollowUpConfigSuccess({ dealerId, followUpConfig: data }));
  } catch (error) {
    yield put(detailsActions.saveFollowUpConfigFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
  }
}

export default function* stepsSaga() {
  yield all([
    fork(stepsDetailsSaga),
    fork(stepsDashboardSaga),
    ...simpleRequestsLoader(simpleRequests),
    takeLatest(types.SAVE_FOLLOW_UP_CONFIG, saveFollowUpConfig),
    takeLatest(types.CHANGE_INTERVENTION_STEP, patchInterventionStep),
  ]);
}
